import { Link } from "react-router-dom";
import { Shield, Mail, Phone, MapPin } from "lucide-react";
import { useNavigation } from "@/hooks/useNavigation";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLanguage } from "@/contexts/LanguageContext";

const Footer = () => {
  const { currentLanguage } = useLanguage();
  const { data: navItems } = useNavigation();
  const { data: settings } = useSiteSettings();

  const phone = settings?.phone || "";
  const email = settings?.email || "";
  const address = settings?.address || "";

  const translations: Record<string, Record<string, string>> = {
    nl: {
      navigation: "Navigatie",
      solutions: "Oplossingen",
      contact: "Contact",
      partner: "Uw betrouwbare partner voor cybersecurity oplossingen. Officieel Stormshield distributeur en Darktrace partner.",
      rights: "Alle rechten voorbehouden.",
      privacy: "Privacy Policy",
      terms: "Algemene Voorwaarden",
      cookies: "Cookie instellingen",
    },
    en: {
      navigation: "Navigation",
      solutions: "Solutions",
      contact: "Contact",
      partner: "Your trusted partner for cybersecurity solutions. Official Stormshield distributor and Darktrace partner.",
      rights: "All rights reserved.",
      privacy: "Privacy Policy",
      terms: "Terms & Conditions",
      cookies: "Cookie settings",
    },
    fr: {
      navigation: "Navigation",
      solutions: "Solutions",
      contact: "Contact",
      partner: "Votre partenaire de confiance pour les solutions de cybersécurité. Distributeur officiel Stormshield et partenaire Darktrace.",
      rights: "Tous droits réservés.",
      privacy: "Politique de confidentialité",
      terms: "Conditions générales",
      cookies: "Paramètres des cookies",
    },
  };

  const t = translations[currentLanguage] || translations.nl;

  // Filter nav items for footer (exclude partner registration)
  const footerNavItems = navItems?.filter(item => !item.href.includes("partner")) || [];

  return (
    <footer className="bg-[hsl(var(--footer-background))] text-[hsl(var(--footer-foreground))]">
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-primary" />
              <div className="flex flex-col">
                <span className="text-lg font-bold tracking-tight text-[hsl(var(--hero-foreground))]">Cyscom</span>
                <span className="text-xs text-[hsl(var(--footer-foreground))] -mt-1">Stormshield & Darktrace Partner</span>
              </div>
            </Link>
            <p className="text-sm text-[hsl(var(--footer-foreground))]">
              {t.partner}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4 text-[hsl(var(--hero-foreground))]">{t.navigation}</h3>
            <ul className="space-y-2">
              {footerNavItems.slice(1).map((item) => (
                <li key={item.id}>
                  <Link to={item.href} className="text-sm text-[hsl(var(--footer-foreground))] hover:text-primary transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-semibold mb-4 text-[hsl(var(--hero-foreground))]">{t.solutions}</h3>
            <ul className="space-y-2">
              <li>
                <span className="text-sm text-[hsl(var(--footer-foreground))]">Network Security</span>
              </li>
              <li>
                <span className="text-sm text-[hsl(var(--footer-foreground))]">Endpoint Protection</span>
              </li>
              <li>
                <span className="text-sm text-[hsl(var(--footer-foreground))]">Data Security</span>
              </li>
              <li>
                <span className="text-sm text-[hsl(var(--footer-foreground))]">Cloud Security</span>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold mb-4 text-[hsl(var(--hero-foreground))]">{t.contact}</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-sm text-[hsl(var(--footer-foreground))]">
                <Mail className="h-4 w-4" />
                <span>{email}</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-[hsl(var(--footer-foreground))]">
                <Phone className="h-4 w-4" />
                <span>{phone}</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-[hsl(var(--footer-foreground))]">
                <MapPin className="h-4 w-4 mt-0.5" />
                <span>{address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-[hsl(var(--hero-foreground)/0.1)] space-y-4">
          <p className="text-xs text-[hsl(var(--footer-foreground))]/60 text-center">
            Stormshield is een handelsmerk van Stormshield SAS. Darktrace en de Darktrace-logo's zijn handelsmerken van Darktrace Holdings Limited. Gebruik conform richtlijnen.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[hsl(var(--footer-foreground))]">
            © {new Date().getFullYear()} Cyscom. {t.rights}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link 
              to={currentLanguage === 'fr' ? '/politique-confidentialite' : currentLanguage === 'en' ? '/privacy-policy' : '/privacy'} 
              className="text-sm text-[hsl(var(--footer-foreground))] hover:text-primary transition-colors"
            >
              {t.privacy}
            </Link>
            <Link 
              to={currentLanguage === 'fr' ? '/conditions-generales' : currentLanguage === 'en' ? '/terms' : '/voorwaarden'} 
              className="text-sm text-[hsl(var(--footer-foreground))] hover:text-primary transition-colors"
            >
              {t.terms}
            </Link>
            <button 
              onClick={() => window.dispatchEvent(new CustomEvent('openCookieSettings'))}
              className="text-sm text-[hsl(var(--footer-foreground))] hover:text-primary transition-colors"
            >
              {t.cookies}
            </button>
            <Link to="/sitemap-overzicht" className="text-sm text-[hsl(var(--footer-foreground))] hover:text-primary transition-colors">
              Sitemap
            </Link>
          </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
